import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Divider = () => (
  <div
    style={{
      borderTop: '1px solid #e2e2dd',
      margin: '0',
    }}
  />
);

export default function PrivacyPolicy() {
  document.title = 'Nagra Accounting | Privacy Policy';
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<any>(null);

  const sections = [
    {
      id: 1,
      title: 'Introduction',
      paragraphs: [
        "Nagra Accounting is a professional accounting firm providing tax filing and related financial services. We are committed to protecting the privacy and confidentiality of our clients' personal and financial information. This Privacy Policy explains how we collect, use, store, and protect your data.",
        'By engaging our services, you agree to the practices described in this policy.',
      ],
    },
    {
      id: 2,
      title: 'Information We Collect',
      paragraphs: [
        'We collect personal and financial information that you voluntarily provide when you engage our services. This may include:',
      ],
      list: [
        'Full name and contact information (email address, phone number).',
        'Financial information necessary for tax filing (income details, deductions, business records).',
        'Government-issued identification numbers as required by law.',
        'Payment and billing information.',
        'Any documents you upload to our platform for processing.',
      ],
    },
    {
      id: 3,
      title: 'How We Collect Your Information',
      paragraphs: [
        'All personal data is collected directly from you when you sign up for our tax filing services. We do not purchase, rent, or obtain contact information from third-party sources. We do not scrape or harvest data from any external website or database.',
        'Client information is entered into our internal system — the Nagra Accounting platform — after you voluntarily provide your details during the onboarding process.',
      ],
    },
    {
      id: 4,
      title: 'How We Use Your Information',
      paragraphs: [
        'Your information is used solely to deliver the services you have requested. Specifically, we use your data to:',
      ],
      list: [
        'Prepare and file your tax returns and related financial documents.',
        'Send automated transactional emails confirming payment receipt.',
        'Notify you when your tax filing has been completed.',
        'Alert you when documents are ready for your review or signature.',
        'Provide status updates related to your tax processing.',
        'Maintain internal records as required by applicable laws and regulations.',
      ],
    },
    {
      id: 5,
      title: 'Our Email Communication Policy',
      paragraphs: [
        'We use email strictly for operational and client-service purposes. All emails sent by Nagra Accounting are transactional in nature — they are directly related to services you have requested from us. We do not send marketing, promotional, or bulk advertising emails.',
        'Our automated emails are triggered by specific service milestones, including:',
      ],
      list: [
        'Payment confirmation.',
        'Confirmation that your tax filing has been completed.',
        'Notification when documents are uploaded and ready for your signature.',
        'Status updates related to your tax processing.',
      ],
    },
    {
      id: 6,
      title: 'Data Sharing & Disclosure',
      paragraphs: [
        'We do not sell, trade, or rent your personal information to any third party. We may share your information only in the following limited circumstances:',
      ],
      list: [
        'Legal requirements: Government or regulatory bodies as required by law (e.g., tax authorities).',
        'Your consent: With your explicit written permission for any other purpose.',
      ],
    },
    {
      id: 7,
      title: 'Data Security',
      paragraphs: [
        'We implement appropriate technical and organizational measures to protect your personal and financial data against unauthorized access, alteration, disclosure, or destruction. Our internal Nagra Accounting platform maintains secure records of all client data.',
        'Despite our efforts, no method of electronic transmission or storage is 100% secure. We encourage you to contact us immediately if you suspect any unauthorized use of your information.',
      ],
    },
    {
      id: 8,
      title: 'Data Retention',
      paragraphs: [
        'We retain your personal and financial information for as long as necessary to fulfill the services provided and to comply with our legal, regulatory, and accounting obligations. Tax records may be retained for a minimum of seven (7) years in accordance with applicable tax laws.',
      ],
    },
    {
      id: 9,
      title: 'Your Rights',
      paragraphs: ['You have the right to:'],
      list: [
        'Request access to the personal data we hold about you.',
        'Request correction of any inaccurate or incomplete information.',
        'Request deletion of your data (subject to legal retention requirements).',
        'Withdraw consent for communications at any time by contacting us.',
      ],
    },
    {
      id: 10,
      title: 'Third-Party Services',
      paragraphs: [
        'Our website or communications may reference third-party services (e.g., Stripe, Mailgun). We are not responsible for the privacy practices of these third parties. We encourage you to review their respective privacy policies.',
      ],
    },
    {
      id: 11,
      title: 'Changes to This Policy',
      paragraphs: [
        'We reserve the right to update this Privacy Policy at any time. Any changes will be posted on our website with a revised effective date. Continued use of our services after such changes constitutes your acceptance of the updated policy.',
      ],
    },
    {
      id: 12,
      title: 'Contact Us',
      paragraphs: [],
      contact: true,
    },
  ];

  return (
    <div
      style={{
        fontFamily: "'Georgia', 'Times New Roman', serif",
        backgroundColor: '#fafaf8',
        minHeight: '100vh',
        color: '#1a1a18',
      }}
    >
      {/* ── HERO ── */}
      <section
        style={{
          backgroundColor: '#1a1a18',
          color: '#fafaf8',
          padding: '68px 32px 60px',
        }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h1
            style={{
              fontSize: 'clamp(30px, 4.5vw, 50px)',
              fontWeight: 'normal',
              letterSpacing: '-0.01em',
              textAlign: 'center',
              lineHeight: '1.15',
              margin: '0 0 20px 0',
              fontFamily: 'Georgia, serif',
            }}
          >
            Privacy Policy
          </h1>
          <p
            style={{
              fontSize: '15px',
              lineHeight: '1.7',
              color: '#9a9a92',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              fontFamily: 'Georgia, serif',
            }}
          >
            We are committed to protecting the privacy and confidentiality of
            our clients' personal and financial information.
          </p>
        </div>
      </section>

      {/* ── BODY ── */}
      <div
        style={{
          maxWidth: '1100px',
          margin: '0 auto',
          padding: '0 32px',
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        {/* Sidebar TOC */}
        <aside
          style={{
            width: '220px',
            flexShrink: 0,
            position: 'sticky',
            top: '88px',
            padding: '52px 36px 52px 0',
            borderRight: '1px solid #e2e2dd',
          }}
        >
          <p
            style={{
              fontSize: '10px',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#aaa8a0',
              margin: '0 0 16px 0',
              fontFamily: 'Georgia, serif',
            }}
          >
            Contents
          </p>
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#section-${s.id}`}
              onClick={() => setActiveSection(s.id)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '5px 0',
                fontSize: '12.5px',
                color: activeSection === s.id ? '#1a1a18' : '#888880',
                textDecoration: 'none',
                lineHeight: '1.45',
                fontFamily: 'Georgia, serif',
                borderLeft:
                  activeSection === s.id
                    ? '2px solid #1a1a18'
                    : '2px solid transparent',
                paddingLeft: activeSection === s.id ? '10px' : '0',
                marginLeft: activeSection === s.id ? '-12px' : '0',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => {
                if (activeSection !== s.id)
                  e.currentTarget.style.color = '#1a1a18';
              }}
              onMouseLeave={(e) => {
                if (activeSection !== s.id)
                  e.currentTarget.style.color = '#888880';
              }}
            >
              <span
                style={{
                  color: '#c8c8c0',
                  fontSize: '11px',
                  flexShrink: 0,
                  marginTop: '1px',
                }}
              >
                {String(s.id).padStart(2, '0')}
              </span>
              {s.title}
            </a>
          ))}
        </aside>

        {/* Main Content */}
        <main
          style={{
            flex: 1,
            paddingLeft: '56px',
            paddingTop: '52px',
            paddingBottom: '80px',
          }}
        >
          {sections.map((s, idx) => (
            <div key={s.id}>
              <div
                id={`section-${s.id}`}
                style={{ paddingTop: '8px', paddingBottom: '44px' }}
              >
                {/* Title row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '16px',
                    marginBottom: '20px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#c0c0b8',
                      fontFamily: 'Georgia, serif',
                      letterSpacing: '0.08em',
                      flexShrink: 0,
                      minWidth: '22px',
                    }}
                  >
                    {String(s.id).padStart(2, '0')}
                  </span>
                  <h2
                    style={{
                      fontSize: '19px',
                      fontWeight: 'normal',
                      color: '#1a1a18',
                      fontFamily: 'Georgia, serif',
                      margin: 0,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {s.title}
                  </h2>
                </div>

                {/* Paragraphs */}
                {s.paragraphs &&
                  s.paragraphs.map((p, i) => (
                    <p
                      key={i}
                      style={{
                        fontSize: '15px',
                        lineHeight: '1.85',
                        color: '#3c3c38',
                        fontFamily: 'Georgia, serif',
                        marginBottom: '12px',
                        marginLeft: '38px',
                        marginTop: 0,
                      }}
                    >
                      {p}
                    </p>
                  ))}

                {/* List */}
                {s.list && (
                  <ul
                    style={{
                      marginLeft: '38px',
                      padding: 0,
                      listStyle: 'none',
                      marginTop: '10px',
                      marginBottom: '12px',
                    }}
                  >
                    {s.list.map((item, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '14px',
                          fontSize: '15px',
                          lineHeight: '1.8',
                          color: '#3c3c38',
                          fontFamily: 'Georgia, serif',
                          marginBottom: '6px',
                        }}
                      >
                        <span
                          style={{
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            backgroundColor: '#c0c0b8',
                            flexShrink: 0,
                            marginTop: '11px',
                          }}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Contact block */}
                {s.contact && (
                  <div style={{ marginLeft: '38px' }}>
                    <p
                      style={{
                        fontSize: '15px',
                        lineHeight: '1.85',
                        color: '#3c3c38',
                        fontFamily: 'Georgia, serif',
                        marginBottom: '24px',
                        marginTop: 0,
                      }}
                    >
                      If you have any questions about this Privacy Policy or how
                      we handle your data, please contact us:
                    </p>
                    <div
                      style={{
                        display: 'inline-block',
                        border: '1px solid #e2e2dd',
                        padding: '24px 32px',
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '10px',
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          color: '#aaa8a0',
                          margin: '0 0 6px 0',
                          fontFamily: 'Georgia, serif',
                        }}
                      >
                        Email
                      </p>
                      <a
                        href="mailto:info@nagraaccounting.com"
                        style={{
                          fontSize: '15px',
                          color: '#1a1a18',
                          textDecoration: 'none',
                          fontFamily: 'Georgia, serif',
                          borderBottom: '1px solid #1a1a18',
                          paddingBottom: '1px',
                        }}
                      >
                        info@nagraaccounting.com
                      </a>
                    </div>
                  </div>
                )}
              </div>
              {idx < sections.length - 1 && <Divider />}
            </div>
          ))}
        </main>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div
        style={{
          borderTop: '1px solid #e2e2dd',
          backgroundColor: '#ffffff',
          padding: '20px 32px',
        }}
      >
        <div
          style={{
            maxWidth: '1100px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontSize: '12px',
              color: '#aaa8a0',
              fontFamily: 'Georgia, serif',
              letterSpacing: '0.03em',
            }}
          >
            © 2026 Nagra Accounting. All rights reserved.
          </span>
        </div>
      </div>
    </div>
  );
}
